import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "@carbon/react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { FileReader } from "../../../components";
import { formatDate } from "../../../utils/utils";
import { CompanyProfileTabProps } from "./OverviewTab";

const NONE = "-";

interface CertificationRow extends DataTableRow {
  certificationName: string;
  expiry: string;
  status: ReactNode;
  actions: ReactNode;
}

/**
 * The Company Data tab's Certifications section — now a Carbon DataTable (certification, expiry
 * date, valid/expired status, download) instead of a bare list. Expiry and validity were already
 * on the certification record but were never shown before.
 */
const CertificationsTab = ({ companyProfile }: CompanyProfileTabProps) => {
  const { t } = useTranslation();
  const certifications = companyProfile.certifications ?? [];

  const rows: CertificationRow[] = certifications.map((certification, index) => ({
    id: certification.id ?? index,
    certificationName: certification.certificationName || NONE,
    expiry: certification.expirationDate
      ? formatDate(certification.expirationDate) || NONE
      : NONE,
    status: (
      <Tag type={certification.valid ? "green" : "red"} size="sm">
        {certification.valid
          ? t("companyProfile.table.valid")
          : t("companyProfile.table.expired")}
      </Tag>
    ),
    actions: certification.fileURL ? (
      <FileReader
        fileKey={certification.fileURL}
        text={t("companyProfile.table.view")}
      />
    ) : (
      NONE
    ),
  }));

  const headers: DataTableColumn<CertificationRow>[] = [
    { key: "certificationName", header: t("companyProfile.table.certification") },
    { key: "expiry", header: t("companyProfile.table.expiry") },
    { key: "status", header: t("companyProfile.table.status") },
    { key: "actions", header: "" },
  ];

  return (
    <div className="company-profile__section">
      <h2 className="company-profile__section-heading">
        {t("companyProfile.certifications")}
      </h2>
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={false}
        empty={
          <EmptyState
            title={t("companyProfile.table.noCertifications")}
            description={t("companyProfile.table.noCertificationsDesc")}
          />
        }
      />
    </div>
  );
};

export default CertificationsTab;
